import type { Language } from "@/contexts/LanguageContext";

// All 24 hockey nations from hockeyNations.ts
export const ALL_NATIONS = [
  "CAN", "USA", "SWE", "FIN", "RUS", "CZE", "CHE", "DEU",
  "SVK", "LVA", "DNK", "NOR", "FRA", "AUT", "KAZ", "BLR",
  "GBR", "HUN", "ITA", "POL", "SVN", "JPN", "KOR", "CHN"
] as const;

export type NationCode = typeof ALL_NATIONS[number];

interface NationTranslation {
  name: string;
  flag: string;
}

// Nation name translations for all supported languages
export const nationTranslations: Record<Language, Record<NationCode, NationTranslation>> = {
  en: {
    CAN: { name: "Canada", flag: "🇨🇦" },
    USA: { name: "United States", flag: "🇺🇸" },
    SWE: { name: "Sweden", flag: "🇸🇪" },
    FIN: { name: "Finland", flag: "🇫🇮" },
    RUS: { name: "Russia", flag: "🇷🇺" },
    CZE: { name: "Czechia", flag: "🇨🇿" },
    CHE: { name: "Switzerland", flag: "🇨🇭" },
    DEU: { name: "Germany", flag: "🇩🇪" },
    SVK: { name: "Slovakia", flag: "🇸🇰" },
    LVA: { name: "Latvia", flag: "🇱🇻" },
    DNK: { name: "Denmark", flag: "🇩🇰" },
    NOR: { name: "Norway", flag: "🇳🇴" },
    FRA: { name: "France", flag: "🇫🇷" },
    AUT: { name: "Austria", flag: "🇦🇹" },
    KAZ: { name: "Kazakhstan", flag: "🇰🇿" },
    BLR: { name: "Belarus", flag: "🇧🇾" },
    GBR: { name: "Great Britain", flag: "🇬🇧" },
    HUN: { name: "Hungary", flag: "🇭🇺" },
    ITA: { name: "Italy", flag: "🇮🇹" },
    POL: { name: "Poland", flag: "🇵🇱" },
    SVN: { name: "Slovenia", flag: "🇸🇮" },
    JPN: { name: "Japan", flag: "🇯🇵" },
    KOR: { name: "South Korea", flag: "🇰🇷" },
    CHN: { name: "China", flag: "🇨🇳" },
  },
  sv: {
    CAN: { name: "Kanada", flag: "🇨🇦" },
    USA: { name: "USA", flag: "🇺🇸" },
    SWE: { name: "Sverige", flag: "🇸🇪" },
    FIN: { name: "Finland", flag: "🇫🇮" },
    RUS: { name: "Ryssland", flag: "🇷🇺" },
    CZE: { name: "Tjeckien", flag: "🇨🇿" },
    CHE: { name: "Schweiz", flag: "🇨🇭" },
    DEU: { name: "Tyskland", flag: "🇩🇪" },
    SVK: { name: "Slovakien", flag: "🇸🇰" },
    LVA: { name: "Lettland", flag: "🇱🇻" },
    DNK: { name: "Danmark", flag: "🇩🇰" },
    NOR: { name: "Norge", flag: "🇳🇴" },
    FRA: { name: "Frankrike", flag: "🇫🇷" },
    AUT: { name: "Österrike", flag: "🇦🇹" },
    KAZ: { name: "Kazakstan", flag: "🇰🇿" },
    BLR: { name: "Vitryssland", flag: "🇧🇾" },
    GBR: { name: "Storbritannien", flag: "🇬🇧" },
    HUN: { name: "Ungern", flag: "🇭🇺" },
    ITA: { name: "Italien", flag: "🇮🇹" },
    POL: { name: "Polen", flag: "🇵🇱" },
    SVN: { name: "Slovenien", flag: "🇸🇮" },
    JPN: { name: "Japan", flag: "🇯🇵" },
    KOR: { name: "Sydkorea", flag: "🇰🇷" },
    CHN: { name: "Kina", flag: "🇨🇳" },
  },
  fi: {
    CAN: { name: "Kanada", flag: "🇨🇦" },
    USA: { name: "Yhdysvallat", flag: "🇺🇸" },
    SWE: { name: "Ruotsi", flag: "🇸🇪" },
    FIN: { name: "Suomi", flag: "🇫🇮" },
    RUS: { name: "Venäjä", flag: "🇷🇺" },
    CZE: { name: "Tšekki", flag: "🇨🇿" },
    CHE: { name: "Sveitsi", flag: "🇨🇭" },
    DEU: { name: "Saksa", flag: "🇩🇪" },
    SVK: { name: "Slovakia", flag: "🇸🇰" },
    LVA: { name: "Latvia", flag: "🇱🇻" },
    DNK: { name: "Tanska", flag: "🇩🇰" },
    NOR: { name: "Norja", flag: "🇳🇴" },
    FRA: { name: "Ranska", flag: "🇫🇷" },
    AUT: { name: "Itävalta", flag: "🇦🇹" },
    KAZ: { name: "Kazakstan", flag: "🇰🇿" },
    BLR: { name: "Valko-Venäjä", flag: "🇧🇾" },
    GBR: { name: "Britannia", flag: "🇬🇧" },
    HUN: { name: "Unkari", flag: "🇭🇺" },
    ITA: { name: "Italia", flag: "🇮🇹" },
    POL: { name: "Puola", flag: "🇵🇱" },
    SVN: { name: "Slovenia", flag: "🇸🇮" },
    JPN: { name: "Japani", flag: "🇯🇵" },
    KOR: { name: "Etelä-Korea", flag: "🇰🇷" },
    CHN: { name: "Kiina", flag: "🇨🇳" },
  },
  ru: {
    CAN: { name: "Канада", flag: "🇨🇦" },
    USA: { name: "США", flag: "🇺🇸" },
    SWE: { name: "Швеция", flag: "🇸🇪" },
    FIN: { name: "Финляндия", flag: "🇫🇮" },
    RUS: { name: "Россия", flag: "🇷🇺" },
    CZE: { name: "Чехия", flag: "🇨🇿" },
    CHE: { name: "Швейцария", flag: "🇨🇭" },
    DEU: { name: "Германия", flag: "🇩🇪" },
    SVK: { name: "Словакия", flag: "🇸🇰" },
    LVA: { name: "Латвия", flag: "🇱🇻" },
    DNK: { name: "Дания", flag: "🇩🇰" },
    NOR: { name: "Норвегия", flag: "🇳🇴" },
    FRA: { name: "Франция", flag: "🇫🇷" },
    AUT: { name: "Австрия", flag: "🇦🇹" },
    KAZ: { name: "Казахстан", flag: "🇰🇿" },
    BLR: { name: "Беларусь", flag: "🇧🇾" },
    GBR: { name: "Великобритания", flag: "🇬🇧" },
    HUN: { name: "Венгрия", flag: "🇭🇺" },
    ITA: { name: "Италия", flag: "🇮🇹" },
    POL: { name: "Польша", flag: "🇵🇱" },
    SVN: { name: "Словения", flag: "🇸🇮" },
    JPN: { name: "Япония", flag: "🇯🇵" },
    KOR: { name: "Южная Корея", flag: "🇰🇷" },
    CHN: { name: "Китай", flag: "🇨🇳" },
  },
  de: {
    CAN: { name: "Kanada", flag: "🇨🇦" },
    USA: { name: "USA", flag: "🇺🇸" },
    SWE: { name: "Schweden", flag: "🇸🇪" },
    FIN: { name: "Finnland", flag: "🇫🇮" },
    RUS: { name: "Russland", flag: "🇷🇺" },
    CZE: { name: "Tschechien", flag: "🇨🇿" },
    CHE: { name: "Schweiz", flag: "🇨🇭" },
    DEU: { name: "Deutschland", flag: "🇩🇪" },
    SVK: { name: "Slowakei", flag: "🇸🇰" },
    LVA: { name: "Lettland", flag: "🇱🇻" },
    DNK: { name: "Dänemark", flag: "🇩🇰" },
    NOR: { name: "Norwegen", flag: "🇳🇴" },
    FRA: { name: "Frankreich", flag: "🇫🇷" },
    AUT: { name: "Österreich", flag: "🇦🇹" },
    KAZ: { name: "Kasachstan", flag: "🇰🇿" },
    BLR: { name: "Belarus", flag: "🇧🇾" },
    GBR: { name: "Großbritannien", flag: "🇬🇧" },
    HUN: { name: "Ungarn", flag: "🇭🇺" },
    ITA: { name: "Italien", flag: "🇮🇹" },
    POL: { name: "Polen", flag: "🇵🇱" },
    SVN: { name: "Slowenien", flag: "🇸🇮" },
    JPN: { name: "Japan", flag: "🇯🇵" },
    KOR: { name: "Südkorea", flag: "🇰🇷" },
    CHN: { name: "China", flag: "🇨🇳" },
  },
  cs: {
    CAN: { name: "Kanada", flag: "🇨🇦" },
    USA: { name: "USA", flag: "🇺🇸" },
    SWE: { name: "Švédsko", flag: "🇸🇪" },
    FIN: { name: "Finsko", flag: "🇫🇮" },
    RUS: { name: "Rusko", flag: "🇷🇺" },
    CZE: { name: "Česko", flag: "🇨🇿" },
    CHE: { name: "Švýcarsko", flag: "🇨🇭" },
    DEU: { name: "Německo", flag: "🇩🇪" },
    SVK: { name: "Slovensko", flag: "🇸🇰" },
    LVA: { name: "Lotyšsko", flag: "🇱🇻" },
    DNK: { name: "Dánsko", flag: "🇩🇰" },
    NOR: { name: "Norsko", flag: "🇳🇴" },
    FRA: { name: "Francie", flag: "🇫🇷" },
    AUT: { name: "Rakousko", flag: "🇦🇹" },
    KAZ: { name: "Kazachstán", flag: "🇰🇿" },
    BLR: { name: "Bělorusko", flag: "🇧🇾" },
    GBR: { name: "Velká Británie", flag: "🇬🇧" },
    HUN: { name: "Maďarsko", flag: "🇭🇺" },
    ITA: { name: "Itálie", flag: "🇮🇹" },
    POL: { name: "Polsko", flag: "🇵🇱" },
    SVN: { name: "Slovinsko", flag: "🇸🇮" },
    JPN: { name: "Japonsko", flag: "🇯🇵" },
    KOR: { name: "Jižní Korea", flag: "🇰🇷" },
    CHN: { name: "Čína", flag: "🇨🇳" },
  },
  fr: {
    CAN: { name: "Canada", flag: "🇨🇦" },
    USA: { name: "États-Unis", flag: "🇺🇸" },
    SWE: { name: "Suède", flag: "🇸🇪" },
    FIN: { name: "Finlande", flag: "🇫🇮" },
    RUS: { name: "Russie", flag: "🇷🇺" },
    CZE: { name: "Tchéquie", flag: "🇨🇿" },
    CHE: { name: "Suisse", flag: "🇨🇭" },
    DEU: { name: "Allemagne", flag: "🇩🇪" },
    SVK: { name: "Slovaquie", flag: "🇸🇰" },
    LVA: { name: "Lettonie", flag: "🇱🇻" },
    DNK: { name: "Danemark", flag: "🇩🇰" },
    NOR: { name: "Norvège", flag: "🇳🇴" },
    FRA: { name: "France", flag: "🇫🇷" },
    AUT: { name: "Autriche", flag: "🇦🇹" },
    KAZ: { name: "Kazakhstan", flag: "🇰🇿" },
    BLR: { name: "Biélorussie", flag: "🇧🇾" },
    GBR: { name: "Grande-Bretagne", flag: "🇬🇧" },
    HUN: { name: "Hongrie", flag: "🇭🇺" },
    ITA: { name: "Italie", flag: "🇮🇹" },
    POL: { name: "Pologne", flag: "🇵🇱" },
    SVN: { name: "Slovénie", flag: "🇸🇮" },
    JPN: { name: "Japon", flag: "🇯🇵" },
    KOR: { name: "Corée du Sud", flag: "🇰🇷" },
    CHN: { name: "Chine", flag: "🇨🇳" },
  },
};

// Onboarding UI translations
export const onboardingTranslations: Record<Language, {
  welcomeTitle: string;
  welcomeSubtitle: string;
  selectPlaceholder: string;
  saveButton: string;
  skipButton: string;
  successMessage: string;
}> = {
  en: {
    welcomeTitle: "Welcome to Ice Ace Hub!",
    welcomeSubtitle: "Choose your favorite national team to personalize your dashboard with their games, players, and alerts.",
    selectPlaceholder: "Select your nation",
    saveButton: "Save & Continue",
    skipButton: "Skip for now",
    successMessage: "Welcome aboard! Your dashboard is now personalized.",
  },
  sv: {
    welcomeTitle: "Välkommen till Ice Ace Hub!",
    welcomeSubtitle: "Välj ditt favoritlandslag för att anpassa din instrumentpanel med deras matcher, spelare och notiser.",
    selectPlaceholder: "Välj din nation",
    saveButton: "Spara & Fortsätt",
    skipButton: "Hoppa över",
    successMessage: "Välkommen! Din instrumentpanel är nu anpassad.",
  },
  fi: {
    welcomeTitle: "Tervetuloa Ice Ace Hubiin!",
    welcomeSubtitle: "Valitse suosikkimaajoukkueesi mukauttaaksesi kojelautaasi heidän peleillään, pelaajillaan ja hälytyksillään.",
    selectPlaceholder: "Valitse maasi",
    saveButton: "Tallenna & Jatka",
    skipButton: "Ohita toistaiseksi",
    successMessage: "Tervetuloa! Kojelautasi on nyt mukautettu.",
  },
  ru: {
    welcomeTitle: "Добро пожаловать в Ice Ace Hub!",
    welcomeSubtitle: "Выберите любимую сборную, чтобы персонализировать панель с их играми, игроками и уведомлениями.",
    selectPlaceholder: "Выберите страну",
    saveButton: "Сохранить и продолжить",
    skipButton: "Пропустить",
    successMessage: "Добро пожаловать! Ваша панель теперь персонализирована.",
  },
  de: {
    welcomeTitle: "Willkommen bei Ice Ace Hub!",
    welcomeSubtitle: "Wählen Sie Ihre Lieblingsnationalmannschaft, um Ihr Dashboard mit deren Spielen, Spielern und Benachrichtigungen zu personalisieren.",
    selectPlaceholder: "Wählen Sie Ihre Nation",
    saveButton: "Speichern & Fortfahren",
    skipButton: "Überspringen",
    successMessage: "Willkommen! Ihr Dashboard ist jetzt personalisiert.",
  },
  cs: {
    welcomeTitle: "Vítejte v Ice Ace Hub!",
    welcomeSubtitle: "Vyberte si oblíbený národní tým pro personalizaci dashboardu s jejich zápasy, hráči a upozorněními.",
    selectPlaceholder: "Vyberte svou zemi",
    saveButton: "Uložit a pokračovat",
    skipButton: "Přeskočit",
    successMessage: "Vítejte! Váš dashboard je nyní přizpůsoben.",
  },
  fr: {
    welcomeTitle: "Bienvenue sur Ice Ace Hub!",
    welcomeSubtitle: "Choisissez votre équipe nationale favorite pour personnaliser votre tableau de bord avec leurs matchs, joueurs et alertes.",
    selectPlaceholder: "Sélectionnez votre nation",
    saveButton: "Enregistrer et continuer",
    skipButton: "Passer pour l'instant",
    successMessage: "Bienvenue! Votre tableau de bord est maintenant personnalisé.",
  },
};

// Helper to get translated nation info
export const getTranslatedNation = (code: string, language: Language) => {
  if (ALL_NATIONS.includes(code as NationCode)) {
    return nationTranslations[language][code as NationCode];
  }
  // Fallback to English for unknown nations
  return { name: code, flag: "" };
};
